import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { M365User } from "../types";
import M365RecycleBin from "./M365RecycleBin";

interface Drive {
  drive_type?: string;
  used_bytes?: number;
  total_bytes?: number;
  remaining_bytes?: number;
}
interface Item {
  id: string;
  name?: string;
  folder: boolean;
  size?: number;
  web_url?: string;
  download_url?: string;
  last_modified?: string;
}
interface Crumb {
  id: string;
  name: string;
}

const CARD = "bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm";
const SUB = "text-xs text-gray-500 dark:text-gray-400";

function fmtBytes(n?: number): string {
  if (n == null) return "—";
  if (n === 0) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
}
function errText(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { detail?: string } } };
  return err.response?.data?.detail || fallback;
}

/**
 * OneDrive management. Pick a user (scoped by the global customer switcher) to
 * see their OneDrive storage, browse/download/delete their files, and open their
 * recycle bin (first + second stage). Backed by
 * /api/v1/m365/{customer_id}/onedrive/{user}/{drive,items,recycle-bin}.
 */
export default function M365OneDrivePanel({
  users,
  customerId,
}: {
  users: M365User[];
  customerId: number | null;
}) {
  const [userId, setUserId] = useState(""); // Entra object id
  const [drive, setDrive] = useState<Drive | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [error, setError] = useState("");
  const [showRecycle, setShowRecycle] = useState(false);

  const tenantUsers = users
    .filter((u) => u.customer_id === customerId)
    .sort((a, b) => (a.display_name || a.upn || "").localeCompare(b.display_name || b.upn || ""));
  const selected = tenantUsers.find((u) => u.object_id === userId);

  const loadItems = useCallback(
    async (itemId: string) => {
      if (!userId) return;
      setLoadingFiles(true);
      setError("");
      try {
        const r = await api.get(`/api/v1/m365/${customerId}/onedrive/${userId}/items`, {
          params: { item_id: itemId },
        });
        setItems(r.data);
      } catch (e) {
        setItems([]);
        setError(errText(e, "Couldn't load files (has the tenant re-consented for Files.ReadWrite.All?)"));
      } finally {
        setLoadingFiles(false);
      }
    },
    [customerId, userId],
  );

  const openUser = async () => {
    if (!userId) return;
    setShowRecycle(false);
    setDrive(null);
    setItems([]);
    setCrumbs([{ id: "root", name: "OneDrive" }]);
    setError("");
    setLoadingFiles(true);
    try {
      const d = await api.get(`/api/v1/m365/${customerId}/onedrive/${userId}/drive`);
      setDrive(d.data);
    } catch (e) {
      setError(errText(e, "Couldn't load OneDrive"));
    }
    loadItems("root");
  };

  const openFolder = (it: Item) => {
    setCrumbs((c) => [...c, { id: it.id, name: it.name || "Folder" }]);
    loadItems(it.id);
  };
  const goCrumb = (idx: number) => {
    const next = crumbs.slice(0, idx + 1);
    setCrumbs(next);
    loadItems(next[next.length - 1].id);
  };
  const del = async (it: Item) => {
    if (!window.confirm(`Delete "${it.name}" from this user's OneDrive? (goes to their recycle bin)`)) return;
    try {
      await api.delete(`/api/v1/m365/${customerId}/onedrive/${userId}/items/${it.id}`);
      loadItems(crumbs[crumbs.length - 1].id);
    } catch (e) {
      setError(errText(e, "Delete failed"));
    }
  };

  useEffect(() => {
    setUserId("");
    setDrive(null);
    setItems([]);
    setCrumbs([]);
    setShowRecycle(false);
  }, [customerId]);

  if (customerId == null) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500 italic">
        Select a customer in the header to manage OneDrive.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Per-user browser */}
      <div className={`${CARD} p-4`}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-72"
          >
            <option value="">Select a user…</option>
            {tenantUsers.map((u) => (
              <option key={u.id} value={u.object_id}>
                {u.display_name || u.upn}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              setShowRecycle(false);
              openUser();
            }}
            disabled={!userId || loadingFiles}
            className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loadingFiles && !drive ? "Loading…" : "Open OneDrive"}
          </button>
          <button
            onClick={() => setShowRecycle((v) => !v)}
            disabled={!userId}
            className={`text-sm px-3 py-1.5 rounded-lg disabled:opacity-50 ${
              showRecycle
                ? "bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100"
                : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
          >
            🗑 Recycle bin
          </button>
        </div>

        {error && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>}

        {showRecycle && userId && (
          <M365RecycleBin customerId={customerId} scope={{ type: "onedrive", userId }} />
        )}

        {!showRecycle && drive && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 mb-1">
              <span>
                {selected?.display_name || selected?.upn} — {fmtBytes(drive.used_bytes)} of{" "}
                {fmtBytes(drive.total_bytes)} used
              </span>
              <span className={SUB}>{fmtBytes(drive.remaining_bytes)} free</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
              <div
                className="h-full bg-blue-500"
                style={{
                  width: `${
                    drive.total_bytes ? Math.min(100, Math.round(((drive.used_bytes || 0) / drive.total_bytes) * 100)) : 0
                  }%`,
                }}
              />
            </div>
          </div>
        )}

        {!showRecycle && crumbs.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-2">
              {crumbs.map((c, i) => (
                <span key={c.id} className="flex items-center gap-1">
                  {i > 0 && <span className="text-gray-300 dark:text-gray-600">/</span>}
                  <button onClick={() => goCrumb(i)} className="hover:underline hover:text-blue-600 dark:hover:text-blue-400">
                    {c.name}
                  </button>
                </span>
              ))}
            </div>
            <div className="border border-gray-100 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
              {loadingFiles ? (
                <p className={`${SUB} px-4 py-6 text-center`}>Loading files…</p>
              ) : items.length === 0 ? (
                <p className={`${SUB} px-4 py-6 text-center`}>Empty folder.</p>
              ) : (
                items.map((it) => (
                  <div
                    key={it.id}
                    className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    title={`${it.name}${it.size != null ? " — " + fmtBytes(it.size) : ""}`}
                  >
                    {it.folder ? (
                      <button
                        onClick={() => openFolder(it)}
                        className="text-sm text-gray-800 dark:text-gray-100 text-left hover:underline"
                      >
                        📁 {it.name}
                      </button>
                    ) : (
                      <span className="text-sm text-gray-700 dark:text-gray-200">📄 {it.name}</span>
                    )}
                    <div className="flex items-center gap-3 text-xs">
                      {!it.folder && it.download_url && (
                        <a
                          href={it.download_url}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </a>
                      )}
                      {it.web_url && (
                        <a
                          href={it.web_url}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open
                        </a>
                      )}
                      <button onClick={() => del(it)} className="text-red-600 dark:text-red-400 hover:underline">
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
